import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpHeadersService } from 'src/app/core/shared-service/http-headers.service';
import { environment } from 'src/environments/environment';
import { LanguageSettings, ProductSettings } from './setting.model';

@Injectable({
  providedIn: 'root'
})
export class ProductSettingsService {

  constructor(
    private http: HttpClient,
    private header: HttpHeadersService
  ) {

  }

  async getGeneralProductSettings(facilityIds: string[], console: string) {
    const headers = this.header.bearerHttpAuthorizationHeader();
    const postData = {facilityIds: facilityIds.toString(), consoleType: console};
    const data = await this.http.post(`${environment.servicePath}/product_settings/get_product_settings`, postData, { headers }).toPromise();
    return this.processGeneralProductSettingsResponse(data);
  }

  async saveGeneralProductSettings(postData: any) {
    const headers = this.header.bearerHttpAuthorizationHeader();
    const data: any = await this.http.post(`${environment.servicePath}/product_settings/save_product_settings`, postData, { headers }).toPromise();
    this.processSetting(data?.assetMgrConfig);
    return data?.assetMgrConfig;
  }

  async publishGeneralProductSettings(postData: any) {
    const headers = this.header.bearerHttpAuthorizationHeader();
    await this.http.post(`${environment.servicePath}/product_settings/publish_product_settings`, postData, { headers }).toPromise();
  }

  async deleteGeneralProductSettings(settingId: number, console: string) {
    const headers = this.header.bearerHttpAuthorizationHeader();
    await this.http.delete(`${environment.servicePath}/product_settings/delete_product_settings?settingsId=${settingId}&consoleType=${console}`, { headers }).toPromise();
  }

  async getWorkoutProductSettings(facilityIds: string[], console: string, activityId: string) {
    const headers = this.header.bearerHttpAuthorizationHeader();
    const postData = {facilityIds: facilityIds.toString(), consoleType: console, activityId: activityId};
    const data = await this.http.post(`${environment.servicePath}/product_settings/workout/get`, postData, { headers }).toPromise();
    return this.processWorkoutProductSettingsResponse(data);
  }

  async saveWorkoutProductSettings(postData: any) {
    const headers = this.header.bearerHttpAuthorizationHeader();
    const data: any = await this.http.post(`${environment.servicePath}/product_settings/workout`, postData, { headers }).toPromise();
    this.processWorkoutSetting(data);
    return data;
  }

  async publishWorkoutProductSettings(postData: any) {
    const headers = this.header.bearerHttpAuthorizationHeader();
    await this.http.post(`${environment.servicePath}/product_settings/workout/publish`, postData, { headers }).toPromise();
  }

  async deleteWorkoutProductSettings(settingId: number, console: string) {
    const headers = this.header.bearerHttpAuthorizationHeader();
    await this.http.delete(`${environment.servicePath}/product_settings/workout?settingsId=${settingId}&consoleType=${console}`, { headers }).toPromise();
  }

  private processGeneralProductSettingsResponse(settings: any) : ProductSettings {
    if(!settings) return settings;
    settings.productSettingsList.forEach((s:any) => this.processSetting(s));
    return settings;
  }

  private processSetting(setting: any) {
    if(!setting) return setting;
    setting.tempData = {};
    setting.id = setting.id?Number(setting.id):setting.id;
    setting.managerId = setting.managerId?Number(setting.managerId):setting.managerId;
    setting.inUse = setting.inUse == 'true';
    setting.displayLoginQRCode = setting.displayLoginQRCode?setting.displayLoginQRCode == 'true':setting.displayLoginQRCode;
    setting.isAudioFeedbackEnabled = setting.isAudioFeedbackEnabled?setting.isAudioFeedbackEnabled == 'true':setting.isAudioFeedbackEnabled;
    setting.isAutoOnOffEnabled = setting.isAutoOnOffEnabled?setting.isAutoOnOffEnabled == 'true':setting.isAutoOnOffEnabled;
    setting.autoOnTime = setting.autoOnTime?Number(setting.autoOnTime):setting.isAutoOnOffEnabled!=null?0:setting.autoOnTime;
    setting.autoOffTime = setting.autoOffTime?Number(setting.autoOffTime):setting.isAutoOnOffEnabled!=null?0:setting.autoOnTime;
    if(Number.isInteger(setting.autoOnTime)) {
      setting.tempData.autoOnHour = this.getHours(setting.autoOnTime);
      setting.tempData.autoOnMinute = this.getMinutes(setting.autoOnTime);
      setting.tempData.autoOnUnit = setting.autoOnTime<720?'AM':'PM';
    }
    if(Number.isInteger(setting.autoOffTime)) {
      setting.tempData.autoOffHour = this.getHours(setting.autoOffTime);
      setting.tempData.autoOffMinute = this.getMinutes(setting.autoOffTime);
      setting.tempData.autoOffUnit = setting.autoOffTime<720?'AM':'PM';
    }
    setting.networkId = setting.networkId?Number(setting.networkId):setting.networkId;
    setting.isNetworkEnabled = setting.isNetworkEnabled?setting.isNetworkEnabled == 'true':setting.isNetworkEnabled;
    setting.isInactivityTimerEnabled = setting.isInactivityTimerEnabled?setting.isInactivityTimerEnabled == 'true':setting.isInactivityTimerEnabled;
    setting.tempData.inactivityHour = this.getHours(setting.inactivityDuration, true);
    setting.tempData.inactivityMinute = this.getMinutes(setting.inactivityDuration);
    setting.isBluetoothEnabled = setting.isBluetoothEnabled?setting.isBluetoothEnabled == 'true':setting.isBluetoothEnabled;
    setting.isLifescapeHomeEnabled = setting.isLifescapeHomeEnabled?setting.isLifescapeHomeEnabled == 'true':setting.isLifescapeHomeEnabled;
    setting.isCustomMessageInWODisplayed = setting.isCustomMessageInWODisplayed == 'true';
    setting.wirelessCharging = setting.wirelessCharging?Number(setting.wirelessCharging):setting.wirelessCharging;
    setting.languageList?.forEach((l:any) => l = this.processLanguageList(l));
  }

  private processLanguageList(language: any): LanguageSettings {
    if(!language) return language;
    language.isDefault = language.isDefault == 'true';
    language.isEnabled = language.isEnabled == 'true';
    return language;
  }

  private getHours(time: number, is24Hr = false) {
		let hour = Math.floor(time/60);
    if(!is24Hr && hour === 0) {
      hour = 12;
    }
		return hour;
	}

	private getMinutes(time: number) {
		const mins = time % 60;
		return mins < 10 ? mins : mins;
	}

  private processWorkoutProductSettingsResponse(settings: any) {
    if(!settings) return settings;
    settings.productSettingsList.forEach((s:any) => this.processWorkoutSetting(s));
    return settings.productSettingsList;
  }

  private processWorkoutSetting(setting: any) {
    if(!setting) return setting;
    setting.tempData = {};
    setting.id = setting.id?Number(setting.id):setting.id;
    setting.managerId = setting.managerId?Number(setting.managerId):setting.managerId;
    setting.inUse = setting.inUse == 'true';
    setting.isPeakTimeEnabled = setting.isPeakTimeEnabled?setting.isPeakTimeEnabled=='true':setting.isPeakTimeEnabled;
    setting.maxWODurationPeak = setting.maxWODurationPeak?Number(setting.maxWODurationPeak):setting.maxWODurationPeak;
    setting.maxWODurationOffPeak = setting.maxWODurationOffPeak?Number(setting.maxWODurationOffPeak):setting.maxWODurationOffPeak;
    setting.programTimeout = setting.programTimeout?Number(setting.programTimeout):setting.programTimeout;
    setting.isExistProgramTimeout = !!setting.programTimeout;
    setting.pauseTime = setting.pauseTime?Number(setting.pauseTime):setting.pauseTime;
    setting.isMarathonModeEnabled = setting.isMarathonModeEnabled?setting.isMarathonModeEnabled=='true':setting.isMarathonModeEnabled;
    setting.isCoachByColorEnabled = setting.isCoachByColorEnabled?setting.isCoachByColorEnabled=='true':setting.isCoachByColorEnabled;
    setting.isFitTestEnabled = setting.isFitTestEnabled?setting.isFitTestEnabled=='true':setting.isFitTestEnabled;
    setting.isTelemetryEnabled = setting.isTelemetryEnabled?setting.isTelemetryEnabled=='true':setting.isTelemetryEnabled;
    setting.isPressAndHoldEnabled = setting.isPressAndHoldEnabled?setting.isPressAndHoldEnabled=='true':true;
    setting.isActivityZoneEnabled = setting.isActivityZoneEnabled?setting.isActivityZoneEnabled=='true':setting.isActivityZoneEnabled;
    setting.isSpeedLimitUpKeyEnabled = setting.isSpeedLimitUpKeyEnabled?setting.isSpeedLimitUpKeyEnabled=='true':setting.isSpeedLimitUpKeyEnabled;
    setting.isStrideSensorEnabled = setting.isStrideSensorEnabled=='true';
    setting.isStrideSensorNotifPauseEnabled = setting.isStrideSensorNotifPauseEnabled?setting.isStrideSensorNotifPauseEnabled=='true':setting.isStrideSensorNotifPauseEnabled;
    setting.accelerationRate = setting.accelerationRate?Number(setting.accelerationRate):0;
    setting.decelerationRate = setting.decelerationRate?Number(setting.decelerationRate):0;
    setting.isWarmUpModeEnabled = setting.isWarmUpModeEnabled?setting.isWarmUpModeEnabled=='true':setting.isWarmUpModeEnabled;
    setting.isUpperBandEnabled = setting.isUpperBandEnabled?setting.isUpperBandEnabled=='true':setting.isUpperBandEnabled;
    setting.hybridMode = setting.hybridMode=='true';
    setting.quickSpeedConfirmation = setting.quickSpeedConfirmation === 'true'

    setting.tempData.workoutDurationMin = 20;
    setting.tempData.workoutDurationMax = 240;
    setting.tempData.pauseTimeMin = 1;
    setting.tempData.pauseTimeMax = 99;
    if(setting.consoleType=="t1" || setting.consoleType=="duplo") {
      setting.tempData.workoutDurationMax = 99;
      setting.tempData.pauseTimeMax = 15;
    }

    setting.tempData.quickSpeedMin = 1;
    if(setting.displayUnits==0) {
      setting.tempData.minSpeedMax = 1.2;
      setting.tempData.displayUnit = 'MPH';
      if(setting.consoleType=="superman" && setting.activityId == '28') {
        setting.tempData.maxSpeedMin = 0.1;
        setting.tempData.maxSpeedMax = '15.0';
        setting.tempData.minSpeedMin = 0.1;
        setting.tempData.quickSpeedMax = 15;
      } else {
        setting.tempData.maxSpeedMin = 0.3;
        setting.tempData.maxSpeedMax =  (setting.consoleType == 'neo' || setting.consoleType == 'neo_se4') && setting.activityId == '1' ? 12.4 : setting.consoleType === 'everest' ? 16.2 : 14;
        setting.tempData.minSpeedMin = 0.3;
        setting.tempData.minSpeedMax = 1.2;
        setting.tempData.quickSpeedMax = 14;
      }
    } else if(setting.displayUnits==1) {
      setting.tempData.quickSpeedMax = 2;
      setting.tempData.displayUnit = 'KPH';
      setting.tempData.minSpeedMin = 0.5;
      setting.tempData.minSpeedMax = 2.0;
      setting.tempData.maxSpeedMin = 0.5;
      setting.tempData.maxSpeedMax = (setting.consoleType == 'neo' || setting.consoleType == 'neo_se4') && setting.activityId == '1' ? 20 : setting.consoleType === 'everest' ? 26 : 23.0;
    }

    if (setting.activeZoneValues) {
      setting.activeZoneSpeedValues = setting.activeZoneValues.filter((item: any) => item).filter((item: any) => item.activeZoneType === 'speed');
      for (let i = setting.activeZoneSpeedValues.length; i < 5; i++) {
        setting.activeZoneSpeedValues.push({activeZoneType: 'speed', activeZoneValue: undefined, activeZoneValueMt: undefined });
      }

      setting.activeZoneInclinesValues = setting.activeZoneValues.filter((item: any) => item).filter((item: any) => item.activeZoneType === 'incline');
      if (setting.activityId === '1' && setting.consoleType === 'everest') {
        for (let i = setting.activeZoneInclinesValues.length; i < 5; i++) {
          setting.activeZoneInclinesValues.push({activeZoneType: 'incline', activeZoneValue: undefined, activeZoneValueMt: undefined });
        }
      }

      if (setting.activityId === '4' && setting.consoleType === 'everest') {
        for (let i = setting.activeZoneInclinesValues.length; i < 3; i++) {
          setting.activeZoneInclinesValues.push({activeZoneType: 'incline', activeZoneValue: undefined, activeZoneValueMt: undefined });
        }
      }

      if (setting.activityId === '2' && setting.consoleType === 'everest') {
        setting.activeZoneLevelsClutchedValues = setting.activeZoneValues.filter((item: any) => item).filter((item: any) => item.activeZoneType === 'level' && item.consoleSubType === 'upright_clutched');
        for (let i = setting.activeZoneLevelsClutchedValues.length; i < 3; i++) {
          setting.activeZoneLevelsClutchedValues.push({activeZoneType: 'level', consoleSubType: 'upright_clutched', activeZoneValue: undefined, activeZoneValueMt: undefined });
        }

        setting.activeZoneLevelsClutchlessValues = setting.activeZoneValues.filter((item: any) => item).filter((item: any) => item.activeZoneType === 'level' && item.consoleSubType === 'upright_clutchless');
        for (let i = setting.activeZoneLevelsClutchlessValues.length; i < 3; i++) {
          setting.activeZoneLevelsClutchlessValues.push({activeZoneType: 'level', consoleSubType: 'upright_clutchless', activeZoneValue: undefined, activeZoneValueMt: undefined });
        }
      } else {
        setting.activeZoneLevelsValues = setting.activeZoneValues.filter((item: any) => item).filter((item: any) => item.activeZoneType === 'level');
        for (let i = setting.activeZoneLevelsValues.length; i < 3; i++) {
          setting.activeZoneLevelsValues.push({activeZoneType: 'level', activeZoneValue: undefined, activeZoneValueMt: undefined });
        }
      }
    }

    this.processPeakTime(setting);
    this.processQuickSpeedValues(setting, setting.quickValues);
    if (setting.quickInclineValues && !Array.isArray(setting.quickInclineValues)) {
      setting.quickInclineValues = [setting.quickInclineValues]
    }
    if(setting.quickValuesNewModel && (setting.consoleType=="superman" || setting.consoleType=="hal" || setting.consoleType=="everest")) {
      this.processQuickSpeedValues(setting, setting.quickValuesNewModel);
    }
    if(setting.quickInclineValues) {
      this.processQuickIncinesValues(setting);
    }
    if (setting.switchCycleValues) {
      const leftList = setting.switchCycleValues.filter((item: any) => item.switchCycleSide === 'left')
      const rightList = setting.switchCycleValues.filter((item: any) => item.switchCycleSide === 'right')

      for (let i = leftList.length; i < 6; i++) {
        setting.switchCycleValues.push({switchCycleSide: 'left', switchCycleValue: undefined, switchCycleValueMt: undefined });
      }

      for (let i = rightList.length; i < 6; i++) {
        setting.switchCycleValues.push({switchCycleSide: 'right', switchCycleValue: undefined, switchCycleValueMt: undefined });
      }
    }
    if(setting.quickResistanceValues) {
      this.processQuickResistenceValues(setting);
    } else if(setting.activityId === '19' && setting.consoleType === 'hal') {
      setting.quickResistanceValues = [];
      this.processQuickResistenceValues(setting);
    }
  }

  private processPeakTime(setting: any) {
    if(!setting.peakTimeList || setting.peakTimeList.length < 1) return;
    setting.peakTimeList[0].tempData = {};
    setting.peakTimeList[1].tempData = {};
    setting.peakTimeList[0].tempData.isEnabled = setting.isPeakTimeEnabled;
    if(setting.peakTimeList.length > 1) {
      let startTime = parseInt(setting.peakTimeList[0].startTime);
      let endTime = parseInt(setting.peakTimeList[0].duration)+startTime;
      setting.peakTimeList[0].tempData.startHour = this.getHours(startTime);
      setting.peakTimeList[0].tempData.startMinute = this.getMinutes(startTime);
      setting.peakTimeList[0].tempData.startUnit = startTime<720?'AM':'PM';
      setting.peakTimeList[0].tempData.endHour = this.getHours(endTime);
      setting.peakTimeList[0].tempData.endMinute = this.getMinutes(endTime);
      setting.peakTimeList[0].tempData.endUnit = endTime<720?'AM':'PM';
      startTime = parseInt(setting.peakTimeList[1].startTime);
      endTime = parseInt(setting.peakTimeList[1].duration)+startTime;
      setting.peakTimeList[1].tempData.startHour = this.getHours(startTime);
      setting.peakTimeList[1].tempData.startMinute = this.getMinutes(startTime);
      setting.peakTimeList[1].tempData.startUnit = startTime<720?'AM':'PM';
      setting.peakTimeList[1].tempData.endHour = this.getHours(endTime);
      setting.peakTimeList[1].tempData.endMinute = this.getMinutes(endTime);
      setting.peakTimeList[1].tempData.endUnit = endTime<720?'AM':'PM';
      setting.peakTimeList[1].tempData.isEnabled = true;
    } else {
      const startTime = parseInt(setting.peakTimeList[0].startTime);
      const endTime = parseInt(setting.peakTimeList[0].duration)+startTime;
      setting.peakTimeList[0].tempData.startHour = this.getHours(startTime);
      setting.peakTimeList[0].tempData.startMinute = this.getMinutes(startTime);
      setting.peakTimeList[0].tempData.sStartUnit = startTime<720?'AM':'PM';
      setting.peakTimeList[0].tempData.endHour = this.getHours(endTime);
      setting.peakTimeList[0].tempData.endMinute = this.getMinutes(endTime);
      setting.peakTimeList[0].tempData.endUnit = endTime<720?'AM':'PM';

      setting.peakTimeList[1].tempData.startHour = 4;
      setting.peakTimeList[1].tempData.startMinute = 0;
      setting.peakTimeList[1].tempData.startUnit = 'PM';
      setting.peakTimeList[1].tempData.eEndHour = 4;
      setting.peakTimeList[1].tempData.endMinute = 0;
      setting.peakTimeList[1].tempData.endUnit = 'PM';
      setting.peakTimeList[1].tempData.isEnabled = false;
    }
  }

  private processQuickIncinesValues(setting: any) {
    for (let i = setting.quickInclineValues.length; i < 6; i++) {
      setting.quickInclineValues.push({'quickValue':undefined});
      }
  }

  private processQuickResistenceValues(setting: any) {
    for (let i = setting.quickResistanceValues.length; i < 6; i++) {
      setting.quickResistanceValues.push({'quickValue':undefined});
      }
  }

  private processQuickSpeedValues(setting: any, quickValues: any[]) {
    if (!quickValues) return;
    let maxlen:number;
    if(setting.consoleType=="hal" || setting.consoleType=="everest"){
      if(setting.activityId == '1' || setting.activityId == '2' || setting.activityId == '4' || setting.activityId == '16'){
        maxlen = 6;
      }else if(setting.activityId == '28'){
        maxlen = 5;
      }else if(setting.consoleType == 'hal' && setting.activityId == '19'){
        maxlen = 6;
      }
      else{
        if(setting.activityId != '18'){
          maxlen = 5;
        }else{
          maxlen = quickValues.length;
        }
      }
      if(quickValues.length > maxlen) {
        quickValues.splice(-1, quickValues.length - maxlen);
      }
      for (let i = quickValues.length; i < maxlen; i++) {
        quickValues.push({'quickValue':undefined, 'quickValueMt':undefined});
      }

    }else if(setting.consoleType=="aquaman" || setting.consoleType=="bluth" || setting.consoleType=="mulder" || setting.consoleType=="superman") {
      maxlen = setting.activityId == '1' || setting.activityId == '28' ||  setting.activityId == '16' ? 4 : setting.activityId != '18' ? 5 : quickValues.length;
      if(quickValues.length > maxlen) {
        quickValues.splice(-1, quickValues.length - maxlen);
      }
      for (let i = quickValues.length; i < maxlen; i++) {
        quickValues.push({'quickValue':undefined, 'quickValueMt':undefined});
      }
    }
  }
}
